import * as S from './styles';
import { useCart } from '@/hooks';
import Modal from '../modal/Modal';
import { colors } from '@/constants';
import { RiInformationLine } from 'react-icons/ri';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { ProductProps, Attributes, PackagesTypesProps, PriceProductProps } from '@/types';

interface ModalCustomAttributesProps {
  isOpen: boolean;
  product: ProductProps | null;
  packQuantity: number;
  pack: PackagesTypesProps | null;
  openModal: Dispatch<SetStateAction<boolean>>;
  price: PriceProductProps | null;
}

const ModalCustomAttributes = ({ product, pack, packQuantity, openModal, isOpen = true, price }: ModalCustomAttributesProps) => {
  const { getStockMsg } = useCart();
  const [currentIsOpen, setCurrentIsOpen] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setCurrentIsOpen(isOpen);
    }, 300);
  }, [isOpen]);

  const attributesLabels = {
    ean_grp: 'EAN',
    dun_grp: 'DUN',
    peso_grp: 'Peso',
    grp_marca: 'Marca',
    familia_grp: 'Família',
    dimensao_grp: 'Dimensão',
    fabricante_401: 'Fabricante',
    volume_104: 'Volume',
    palavra_chave_grp: 'Palavra-chave',
    description: 'Especificação detalhada',
    cod_promocao: 'Cód. Promoção',
    formato_101: 'Formato',
    fragrância_201: 'Fragrância',
    tipo_81: 'Tipo',
    embalagem_105: 'Embalagem',
  };

  const hasExpiring = useMemo(() => {
    if (!product?.COD_PRODUTO) return false;
    return getStockMsg(product?.COD_PRODUTO);
  }, [getStockMsg, product?.COD_PRODUTO]);

  const expiringSeverity = useMemo<'danger' | 'warning' | 'normal'>(() => {
    const msg = hasExpiring || '';
    const match = msg.match(/(\d+)/);
    const days = match ? Number(match[1]) : NaN;
    if (!isNaN(days)) {
      if (days <= 90) return 'danger';
      if (days <= 120) return 'warning';
    }
    return 'normal';
  }, [hasExpiring]);

  const dun = useMemo(() => {
    return product?.EMBALAGEM?.find((i) => i.SG_EMBALAGEM === pack && i.QTD_EMBALAGEM * i.QTD_MULTIPLO_VENDA === packQuantity)
      ?.DUN_PRODUTO;
  }, [product, pack, packQuantity]);

  let ean = { ean_grp: '' };

  const atributos = useMemo(() => {
    if (!product?.ATRIBUTOS) return [];

    const list = product?.ATRIBUTOS?.filter((i) => {
      const key = Object.keys(i)?.[0] as Attributes;
      const isValid = i?.[key] !== null;
      if (key === 'ean_grp' && isValid) ean = { ean_grp: i?.[key]! };
      return isValid && key !== 'ean_grp';
    });

    if (!!price?.COD_PROMOCAO) list.unshift({ cod_promocao: `${price.COD_PROMOCAO}` });
    if (dun && dun.toString() !== '0') list.unshift({ dun_grp: dun.toString() });
    list.unshift(ean);


    return list;
  }, [product, dun]);

  return (
    <Modal
      id={`modal-custom-attributes-${product?.COD_PRODUTO ?? ''}`}
      direction="right"
      isOpen={currentIsOpen}
      isScrollable={false}
      closeModal={openModal}
    >
      {currentIsOpen && product && pack && price && (
        <>
          <S.Title>Detalhes do produto</S.Title>
          <S.Divider />
          <S.ModalContent>
            <S.ProductIdGrid>
              <S.ProductImageWrapper $hasExpiring={!!hasExpiring}>
                <S.ProductImage alt={product.DESC_PRODUTO} src={`data:image/jpg;base64,${product.IMAGEM}`} />
              </S.ProductImageWrapper>
              <S.ProductName $hasExpiring={!!hasExpiring} $severity={expiringSeverity}>
                {product.DESC_PRODUTO}
              </S.ProductName>
            </S.ProductIdGrid>

            {hasExpiring && (
              <S.ExpiringView>
                <RiInformationLine size={18} color={expiringSeverity === 'danger' ? colors.alert.red200 : colors.alert.orange300} />

                <S.ExpiringText $severity={expiringSeverity}>{hasExpiring}</S.ExpiringText>
              </S.ExpiringView>
            )}

            <S.ProductTable>
              <S.TableBody>
                {atributos?.map((item, index) => {
                  const key = Object.keys(item)?.[0] as Attributes;
                  const value = item[key];
                  if (key && value) {
                    if (!!attributesLabels[key as keyof typeof attributesLabels]) {
                      return (
                        <S.TableRow key={`${key}-${index}`} $applyBackground={(index + 1) % 2 !== 0}>
                          <S.TableCell $bold={'true'}>{attributesLabels[key as keyof typeof attributesLabels]}</S.TableCell>
                          <S.TableCell>{value}</S.TableCell>
                        </S.TableRow>
                      );
                    } else {
                      return (
                        <S.TableRow key={`${key}-${index}`} $applyBackground={(index + 1) % 2 !== 0}>
                          <S.TableCell $bold={'true'} $capitalize={true}>
                            {key?.split('_')?.[0]}
                          </S.TableCell>
                          <S.TableCell>{value}</S.TableCell>
                        </S.TableRow>
                      );
                    }
                  }
                  return null;
                })}
              </S.TableBody>
            </S.ProductTable>
          </S.ModalContent>
        </>
      )}
    </Modal>
  );
};

export default ModalCustomAttributes;
